import React, { useState } from 'react';
import './ProductGuide.css';

import landingImg from '../../assets/images/guide-landing.png';
import signupImg from '../../assets/images/guide-signup.png';
import dashboardImg from '../../assets/images/guide-dashboard.png';
import contactsImg from '../../assets/images/guide-contacts.png';
import templatesImg from '../../assets/images/guide-templates.png';
import broadcastImg from '../../assets/images/guide-broadcast.png';

const steps = [
  {
    title: 'Get Started — Landing Page',
    markerClass: 'step-1',
    badge: { text: 'Start here', color: '#2563eb' },
    image: landingImg,
    body: (
      <>
        <p>
          Visit the landing page and click <strong>Get Started</strong> to begin.
          If you already have an account, click <strong>Log In</strong> to sign in directly.
        </p>
        <div className="guide-features">
          <span className="guide-chip">One-click signup</span>
          <span className="guide-chip green">Built on official WhatsApp Business API</span>
        </div>
      </>
    ),
    actions: [
      'Open the main website and review plans briefly.',
      'Click Get Started for new signup or Log In for existing account.',
      'Confirm your business use case before moving ahead.',
    ],
    note: 'Starting with the right entry point helps your team avoid setup confusion later.',
    outcome: 'You enter the platform with the right flow in under a minute.',
  },
  {
    title: 'Create Your Account',
    markerClass: 'step-2',
    badge: { text: 'Account', color: '#7c3aed' },
    image: signupImg,
    body: (
      <>
        <p>
          Register with your company name, contact name, and mobile number or email.
          Verify via OTP — no complicated forms, no credit card required.
        </p>
        <div className="guide-features">
          <span className="guide-chip purple">Mobile OTP</span>
          <span className="guide-chip purple">Email OTP</span>
          <span className="guide-chip">reCAPTCHA protected</span>
        </div>
      </>
    ),
    actions: [
      'Fill company details exactly as used in your business documents.',
      'Complete OTP verification on preferred channel.',
      'Save login details and continue to dashboard.',
    ],
    note: 'Clean account details reduce approval delays in later Meta verification steps.',
    outcome: 'Your workspace is activated and ready for channel setup.',
  },
  {
    title: 'Dashboard & WhatsApp Setup',
    markerClass: 'step-3',
    badge: { text: 'Dashboard', color: '#0891b2' },
    image: dashboardImg,
    body: (
      <>
        <p>
          After login you land on the Dashboard — your real-time command centre.
          Check delivery rates, messages sent, active campaigns, and wallet balance at a glance.
        </p>
        <div className="guide-tip">
          <span className="guide-tip-icon">💡</span>
          <span>
            First, connect your WhatsApp Business Account (WABA).
            Look at the top-right status — if it says <strong>"Not Connected"</strong>,
            click it and follow the setup wizard.
            Hit the <strong>"?"</strong> button for a step-by-step flow.
          </span>
        </div>
        <div className="guide-features" style={{ marginTop: 12 }}>
          <span className="guide-chip">Delivery rate</span>
          <span className="guide-chip green">Wallet balance</span>
          <span className="guide-chip orange">Campaign stats</span>
        </div>
      </>
    ),
    actions: [
      'Check top-level stats to confirm dashboard is loading correctly.',
      'Click Not Connected and start WABA onboarding.',
      'Use the header help button when setup prompts appear.',
    ],
    note: 'Connecting WABA first unlocks templates, broadcasts, and live messaging workflows.',
    outcome: 'Your account moves from setup mode to messaging-ready mode.',
  },
  {
    title: 'Manage Contacts',
    markerClass: 'step-4',
    badge: { text: 'Contacts', color: '#16a34a' },
    image: contactsImg,
    body: (
      <>
        <p>
          Open <strong>Contacts</strong> from the sidebar to manage your address book.
          Add contacts one-by-one or import in bulk using CSV/Excel —
          sample files are provided so you can match the format instantly.
        </p>
        <div className="guide-features">
          <span className="guide-chip green">Single add</span>
          <span className="guide-chip green">Bulk CSV/Excel import</span>
          <span className="guide-chip">Labels &amp; directories</span>
        </div>
      </>
    ),
    actions: [
      'Prepare CSV/Excel with country codes and clean phone numbers.',
      'Import contacts and verify count after upload.',
      'Assign labels/directories for future segmentation.',
    ],
    note: 'A structured contact base directly improves delivery quality and campaign targeting.',
    outcome: 'Your audience is organized for fast campaign selection.',
  },
  {
    title: 'Create & Manage Templates',
    markerClass: 'step-5',
    badge: { text: 'Templates', color: '#ea580c' },
    image: templatesImg,
    body: (
      <>
        <p>
          Go to <strong>Templates</strong> to browse pre-approved WhatsApp templates
          or create your own. Custom templates are submitted to Meta for approval —
          once approved, they're ready to use in campaigns.
        </p>
        <div className="guide-features">
          <span className="guide-chip orange">Pre-approved library</span>
          <span className="guide-chip orange">Custom templates</span>
          <span className="guide-chip">Meta approval flow</span>
        </div>
      </>
    ),
    actions: [
      'Start with pre-approved templates for immediate sends.',
      'Create custom templates for brand-specific campaigns.',
      'Track Meta approval status and update rejected copies quickly.',
    ],
    note: 'Template quality determines both approval speed and campaign performance.',
    outcome: 'You build a reusable template library for repeated communication.',
  },
  {
    title: 'Broadcast Messages',
    markerClass: 'step-6',
    badge: { text: 'Broadcast', color: '#dc2626' },
    image: broadcastImg,
    body: (
      <>
        <p>
          Head to <strong>Broadcast</strong> to launch campaigns.
          Create a campaign, select your contacts or directory,
          pick a template, and send — track delivery and read status in real time.
        </p>
        <div className="guide-features">
          <span className="guide-chip">Campaign manager</span>
          <span className="guide-chip green">Real-time tracking</span>
          <span className="guide-chip purple">Template selection</span>
        </div>
      </>
    ),
    actions: [
      'Create campaign and choose the correct contact segment.',
      'Attach an approved template and schedule or send instantly.',
      'Monitor sent, delivered, and read metrics in real time.',
    ],
    note: 'Broadcast flow ties together contacts, templates, and analytics for measurable growth.',
    outcome: 'Your first end-to-end campaign goes live with clear tracking.',
  },
];

const ProductGuide = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const activeStep = steps[currentStep];
  const totalSteps = steps.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      setCurrentStep(0);
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  return (
    <div className="guide-wrapper">
      <div className="guide-header">
        <h1>Product Guide</h1>
        <p>A quick walkthrough of MessageOS — from signup to your first broadcast.</p>
      </div>

      <div className="guide-step-tabs" role="tablist" aria-label="Guide steps">
        {steps.map((step, idx) => (
          <button
            key={step.title}
            type="button"
            role="tab"
            aria-selected={idx === currentStep}
            aria-label={`Go to step ${idx + 1}: ${step.title}`}
            className={`guide-step-tab ${idx === currentStep ? 'active' : ''}`}
            onClick={() => setCurrentStep(idx)}
            style={idx === currentStep ? { borderColor: step.badge.color, color: step.badge.color } : undefined}
          >
            {idx + 1}. {step.badge.text}
          </button>
        ))}
      </div>

      <div className="guide-stage" style={{ '--guide-accent': activeStep.badge.color }}>
        {activeStep.image && (
          <div className="guide-stage-media">
            <img src={activeStep.image} alt={activeStep.title} />
            {activeStep.badge && (
              <span className="guide-img-badge" style={{ background: activeStep.badge.color }}>
                {activeStep.badge.text}
              </span>
            )}
          </div>
        )}

        <div className="guide-stage-content">
          <h2>{activeStep.title}</h2>
          <div className="guide-stage-body">
            {activeStep.body}

            <div className="guide-detail-panels">
              <div className="guide-detail-panel">
                <h4>Action Checklist</h4>
                <ul>
                  {activeStep.actions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="guide-detail-panel subtle">
                <h4>Why This Step Matters</h4>
                <p>{activeStep.note}</p>
                <p className="guide-outcome">
                  <strong>Outcome:</strong> {activeStep.outcome}
                </p>
              </div>
            </div>
          </div>

          <div className="guide-stage-nav">
            <button
              type="button"
              className="guide-nav-btn secondary"
              onClick={handlePrev}
              disabled={isFirstStep}
            >
              Back
            </button>
            <div className="guide-stage-counter">{currentStep + 1} / {totalSteps}</div>
            <button
              type="button"
              className="guide-nav-btn primary"
              onClick={handleNext}
            >
              {isLastStep ? 'Start Again' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      <div className="guide-mobile-nav">
        <button
          type="button"
          className="guide-nav-btn secondary"
          onClick={handlePrev}
          disabled={isFirstStep}
        >
          Back
        </button>
        <button
          type="button"
          className="guide-nav-btn primary"
          onClick={handleNext}
        >
          {isLastStep ? 'Start Again' : 'Next'}
        </button>
      </div>

      <div className="guide-mobile-counter">
        {currentStep + 1} / {totalSteps}
      </div>
    </div>
  );
};

export default ProductGuide;
